// Handing a code or a link to somebody else (arcade.md §15.5, §16.1).
//
// Two things in the arcade are meant to be passed on: the three symbols of
// a hosting code, and the address of a match replay. A telephone has a
// system share sheet for the second one, and a desktop has the clipboard
// for both. Neither browser has both: `navigator.share` is absent on most
// desktops, and a share sheet for three letters is a sheet nobody wants.
//
// So the rule is one line: a LINK on a coarse pointer goes to the share
// sheet, and everything else goes to the clipboard. A refusal — a browser
// with no clipboard, a page with no permission, a reader who dismissed
// the sheet — says so in one sentence and changes nothing.
//
// `deps` is the whole of what this module touches outside itself, so the
// test drives every branch with plain objects and no browser.

import { share as S } from '../gen/strings';
import { coarsePointer } from './pointer';

/** What is being handed on. A code is short and typed back in by hand; a
 *  link is long and opens by itself. */
export type ShareKind = 'code' | 'link';

/** The browser surface the helper uses. */
export interface ShareDeps {
  /** True on a finger-driven pointer, which is where a share sheet lives. */
  coarse: boolean;
  /** The system share sheet, or null where the browser has none. */
  share: ((data: { url: string }) => Promise<void>) | null;
  /** The clipboard, or null where the browser has none. */
  write: ((text: string) => Promise<void>) | null;
}

/** The real browser. It is read at the call and never at module load, so a
 *  page that rotates into a touch lane gets the right answer. */
export function fromNavigator(): ShareDeps {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  return {
    coarse: coarsePointer(),
    share: nav && typeof nav.share === 'function'
      ? (data) => nav.share(data) : null,
    write: nav?.clipboard && typeof nav.clipboard.writeText === 'function'
      ? (text) => nav.clipboard.writeText(text) : null,
  };
}

/**
 * True when a LINK handed on here goes to the system share sheet: the same
 * test `shareText` makes. A control that hands a link on reads SHARE when
 * this is true and COPY LINK when it is false, so the label names what the
 * press will do.
 */
export function canShareSheet(deps: ShareDeps = fromNavigator()): boolean {
  return deps.coarse && deps.share !== null;
}

/**
 * Hands `text` on, and tells the reader what happened.
 *
 * `notify` is the sink: the notice lane inside a match or a viewer, and a
 * `.share-note` line in a lobby panel. It is always called exactly once,
 * because a control that answers nothing reads as a control that did not
 * work.
 */
export async function shareText(text: string, kind: ShareKind,
  notify: (line: string) => void, deps: ShareDeps = fromNavigator()): Promise<void> {
  const outcome = await shareOutcome(text, kind, deps);
  notify(outcome === 'shared' ? S.shared
    : outcome === 'copied' ? (kind === 'code' ? S.copied_code : S.copied_link)
      : S.copy_failed);
}

/** The sink a panel calls: `shareText` without the promise. A panel takes
 *  it as the default of its own `deps.share`, so a test passes a fake. */
export function handOn(text: string, kind: ShareKind, notify: (line: string) => void): void {
  void shareText(text, kind, notify);
}

// --- the outcome, for a caller that draws its own answer (R1206) ----------

/** What became of one hand-on: the share sheet took it, the clipboard took
 *  it, or neither did. */
export type ShareOutcome = 'shared' | 'copied' | 'failed';

/**
 * Hands `text` on and answers what happened instead of a sentence. It is
 * the one hand-on: `shareText` words its answer, and the chronicle book
 * reads it as it is, because its COPY LINK press changes its own label
 * and shows the address in a box to select by hand where both ways
 * refused (chronicle.md §9).
 */
export async function shareOutcome(text: string, kind: ShareKind,
  deps: ShareDeps = fromNavigator()): Promise<ShareOutcome> {
  if (kind === 'link' && canShareSheet(deps) && deps.share) {
    try {
      await deps.share({ url: text });
      return 'shared';
    } catch {
      // A dismissed sheet and a refused share are the same thing here: the
      // reader still wants the address, so the clipboard is tried next.
    }
  }
  return (await copyText(text, deps)) ? 'copied' : 'failed';
}

/** Copies `text` to the clipboard, and answers true when the clipboard
 *  took it: false where the browser has none or refuses. The COPY of the
 *  REPORT sheet reads it. */
export async function copyText(text: string, deps: ShareDeps = fromNavigator()): Promise<boolean> {
  if (!deps.write) return false;
  try {
    await deps.write(text);
    return true;
  } catch {
    return false;
  }
}
